import {
  AccountReceivablesService,
  AccountReceivable,
  Workflow,
  WorkflowsService,
  OrganizationService,
  OrgStripeProduct
  } from '@/api';
  import { Button } from '@/components/ui/button';
  import { cn } from '@/lib/utils';
  import { useStore } from '@nanostores/react';
  import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
  import {
    ArrowLeft,
    FileText,
    FileUp,
    FolderIcon,
    Loader2,
    X,
  } from 'lucide-react';
  import { toast } from 'sonner';
  import { useState, useEffect } from 'react';
  import { Checkbox } from '@/components/ui/checkbox';
  import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
  } from '@/components/ui/select';
  import { Input } from '@/components/ui/input';


  interface Props {
    open: boolean;
    setOpen: React.Dispatch<React.SetStateAction<boolean>>;
    setNodes: any;
    selectedNode: any;
    workFlowId: string;
  }


    
  const fetchAccountReceivables = async (workflow: Workflow) => {
    if (!workflow.executionData?.portfolioIds?.length) return [];
  
    // Get unique portfolio IDs from execution data
    const portfolioIds = [...new Set(
      workflow.executionData?.portfolioIds?.map(data => data)
    )];
  
    // Fetch accounts for each portfolio ID in parallel
    const accountsPromises = portfolioIds.map(portfolioId =>
      AccountReceivablesService.getAllAccountReceivables(portfolioId)
    );
  
    const accountResults = await Promise.all(accountsPromises);
    
    // Flatten and deduplicate accounts
    const allAccounts = accountResults.flat();
    const uniqueAccounts = [...new Set(allAccounts.map(a => a.id))]
      .map(id => allAccounts.find(a => a.id === id)!);

    // Get workflow entity links to filter out accounts already in workflow
    const workflowEntityLinks = await WorkflowsService.getWorkflowEntityLinks(workflow.id);
    
    // Get set of account IDs that are already in the workflow
    const existingAccountIds = new Set(workflowEntityLinks.map(link => link.entityId));

    // Filter out accounts that are already in the workflow
    const eligibleAccounts = uniqueAccounts.filter(account => !existingAccountIds.has(account.id));
  
    return eligibleAccounts;
  };
  
  export default function WorkflowManualRunDrawer({
    open,
    setOpen,
    setNodes,
    selectedNode,
    workFlowId,
  }: Props) {
    const org = useStore($org);
    const workflow = useStore($workflow);
    const [accounts, setAccounts] = useState<AccountReceivable[]>([]);
    const [selectedAccountIds, setSelectedAccountIds] = useState<string[]>([]);
    const [orgStripeProducts, setOrgStripeProducts] = useState<OrgStripeProduct[]>([]);
    const [selectedProductIds, setSelectedProductIds] = useState<string[]>([]);
    const [minAmount, setMinAmount] = useState<string>('');
    const [maxAmount, setMaxAmount] = useState<string>('');
    const [loading, setLoading] = useState(false);
    
    useEffect(() => {
      if (workflow) {
        fetchAccountReceivables(workflow).then(setAccounts);
      }
    }, [workflow]);

    useEffect(() => {
      const fetchStripeProducts = async () => {
        if (org?.id) {
          const products = await OrganizationService.getStripeProducts(org.id);
          setOrgStripeProducts(products);
        }
      };
      
      fetchStripeProducts();
    }, [org?.id]);

    // Reset state when workflow changes or component unmounts
    useEffect(() => {
      // Reset all state when workflow changes
      setSelectedProductIds([]);
      setSelectedAccountIds([]);
      setOrgStripeProducts([]);

      // Cleanup function to reset state when component unmounts
      return () => {
        setSelectedProductIds([]);
        setSelectedAccountIds([]);
        setOrgStripeProducts([]);
      };
    }, [workflow?.id]); // Add workflow.id as dependency to reset when workflow changes

    const handleSelectAll = () => {
      if (selectedAccountIds.length === filteredAccounts.length) {
        setSelectedAccountIds([]);
      } else {
        setSelectedAccountIds(filteredAccounts.map(account => account.id));
      }
    };

    const handleAccountSelect = (accountId: string) => {
      setSelectedAccountIds(prev => 
        prev.includes(accountId)
          ? prev.filter(id => id !== accountId) // Remove if selected
          : [...prev, accountId] // Add if not selected
      );
    };

    const handleProductSelect = (stripeProductId: string) => {
      setSelectedProductIds(prev => 
        prev.includes(stripeProductId) 
          ? prev.filter(id => id !== stripeProductId)
          : [...prev, stripeProductId]
      );
    };

    const filteredAccounts = accounts.filter(account => {
      // Filter by product
      if (selectedProductIds.length > 0 && !selectedProductIds.includes(account.stripeProductId ?? '')) {
        return false;
      }

      // Get the amount to filter by (either amount due or Stripe plan amount)
      const stripeProduct = orgStripeProducts.find(p => p.stripeProductId === account.stripeProductId);
      const amount = account.amountDue > 0 ? account.amountDue : 
                    (stripeProduct?.orgStripePricingProducts?.[0]?.amount ?? 0);

      // Filter by amount range
      if (minAmount && amount < parseFloat(minAmount)) return false;
      if (maxAmount && amount > parseFloat(maxAmount)) return false;
      return true;
    });

    const fetchAccounts = async () => {
      if (!workflow?.executionData?.portfolioIds?.length) return;
      
      setLoading(true);
      try {
        const portfolioIds = [...new Set(workflow.executionData?.portfolioIds)];
        const accountsPromises = portfolioIds.map(portfolioId =>
          AccountReceivablesService.getAllAccountReceivables(portfolioId)
        );
        
        const accountResults = await Promise.all(accountsPromises);
        const allAccounts = accountResults.flat();
        const uniqueAccounts = [...new Set(allAccounts.map(a => a.id))]
          .map(id => allAccounts.find(a => a.id === id)!);
        
        console.log('Fetched accounts:', uniqueAccounts);
        setAccounts(uniqueAccounts);
      } catch (error) {
        toast.error('Failed to fetch accounts');
      } finally {
        setLoading(false);
      }
    };

    const handleRun = async () => {
      if (selectedAccountIds.length === 0) {
        toast.error("Please select at least one account");
        return;
      }

      try {
        if (workflow?.executionFrequencyType == null || workflow?.timeZone == null) {
          const missing = [];
          if (workflow?.executionFrequencyType == null) missing.push("frequency type");
          if (workflow?.timeZone == null) missing.push("timezone");
          
          toast.error(`Please set ${missing.join(" and ")} before running the workflow. Please set frequency type to Manual.`);
          return;
        }
        toast.success("Starting workflow execution(s)...");
        setOpen(false); // Close drawer after successful execution

        await WorkflowsService.manualKickOffBulkWorkflowExecution(workFlowId, {
          accountReceivableIds: selectedAccountIds
        });

      } catch (error) {
        toast.error("Failed to start workflow execution");
        console.error(error);
      }
    };

    return (
      <div className={`absolute right-6 top-20 min-h-[160px] min-w-[480px] rounded-lg bg-white p-4 pl-6 pr-6 ${!open && 'hidden'} z-10`}>
        <div className="flex justify-between">
          <div className="flex items-center">
            <p className="text-sm font-semibold tracking-tight">
              Workflow Manual Run
            </p>
          </div>
          <button
            type="button"
            onClick={() => setOpen(false)}
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="mt-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold">Filter by Amount:</h3>
            <div className="flex items-center space-x-2">
              <Input
                type="number"
                placeholder="Min $"
                value={minAmount}
                onChange={(e) => setMinAmount(e.target.value)}
                className="w-[100px]"
              />
              <span className="text-muted-foreground">-</span>
              <Input
                type="number"
                placeholder="Max $"
                value={maxAmount}
                onChange={(e) => setMaxAmount(e.target.value)}
                className="w-[100px]"
              />
            </div>
          </div>
        </div>

        {orgStripeProducts.length > 0 && (
          <div className="mt-4">
            <h3 className="text-sm font-semibold mb-3">Filter by Stripe Products:</h3>
            <div className="space-y-2 max-h-[200px] overflow-y-auto">
              {orgStripeProducts.map(product => (
                <div key={product.stripeProductId} className="flex items-center">
                  <Checkbox
                    checked={selectedProductIds.includes(product.stripeProductId)}
                    onCheckedChange={() => handleProductSelect(product.stripeProductId)}
                  />
                  <label className="ml-2 text-sm font-medium">
                    {product.name}
                  </label>
                </div>
              ))}
            </div>
          </div>
        )}

        {filteredAccounts.length > 0 && (
          <div className="mt-4">
            <h3 className="text-sm font-semibold mb-3">Accounts:</h3>
            
            <div className="flex items-center mb-2">
              <Checkbox
                checked={selectedAccountIds.length === filteredAccounts.length}
                onCheckedChange={handleSelectAll}
              />
              <label className="ml-2 text-sm font-medium">
                Select All ({filteredAccounts.length})
              </label>
            </div>

            <div className="space-y-2 max-h-[300px] overflow-y-auto">
              {filteredAccounts.map(account => {
                console.log('Account data:', account);
                return (
                  <div key={account.id} className="flex items-center justify-between">
                    <div className="flex items-center">
                      <Checkbox
                        checked={selectedAccountIds.includes(account.id)}
                        onCheckedChange={() => handleAccountSelect(account.id)}
                      />
                      <div className="ml-2 text-sm font-medium">
                        {account.customer?.contactEmail}
                      </div>
                      <div className="ml-2 text-sm text-muted-foreground">
                        {orgStripeProducts.find(p => p.stripeProductId === account.stripeProductId)?.name ?? 'N/A'}
                        {account.amountDue !== undefined && account.amountDue !== null && account.amountDue > 0
                          ? ` ($${account.amountDue.toFixed(2)})`
                          : account.balance !== undefined && account.balance !== null && account.balance > 0
                            ? ` ($${account.balance.toFixed(2)})`
                            : ''
                        }
                      </div>
                    </div>
                    <Button variant="secondary" size="sm" className="h-6">
                      {account.status}
                    </Button>
                  </div>
                );
              })}
            </div>

            <div className="mt-4 flex justify-end">
              <Button
                onClick={handleRun}
                disabled={selectedAccountIds.length === 0}
              >
                Run Workflow
              </Button>
            </div>
          </div>
        )}

        {accounts.length === 0 && (
          <div className="mt-4 text-sm text-muted-foreground">
            No accounts found for this workflow.
          </div>
        )}
      </div>
    );
  }
  